import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { loadMatches, loadMatchesFailure, loadMatchesSuccess } from './fixtures.actions';
import { catchError, map, switchMap } from 'rxjs/operators';
import { combineLatest, of } from 'rxjs';
import { StreamPlayService } from '@streamamg/stream-play-lib';
import { configs } from '../../configs';
import { Team } from '../../services/cebl.api.types';
import { StreamPlayItem } from '@streamamg/stream-play-lib/src/lib/types/stream-play.type';
import { selectTeamsMap, selectTeamsMatchesMap } from '../teams/teams.selectors';
import { Store } from '@ngrx/store';
import { AppState } from '../index';

@Injectable()
export class FixturesEffects {

  loadMatches$ = createEffect(() => this.actions$.pipe(
    ofType(loadMatches),
    switchMap((params) => combineLatest([
      this.store.select(selectTeamsMap),
      this.store.select(selectTeamsMatchesMap),
      this.streamPlayService.triggerApiCall(configs.streamPlayEndPoint, 200, null, {q: '(type:fixture)'}, false, null, true),
    ])),
    map(([teamsMap, teamsMatchesMap, streamPlayState]) => {

      let filteredMatches: StreamPlayItem[] = streamPlayState?.data;
      if (teamsMatchesMap?.size > 0) {
        filteredMatches = this.filterPriorityMatches(filteredMatches, teamsMap, teamsMatchesMap);
      } else {
        filteredMatches = this.filterMatches(filteredMatches, teamsMap);
      }

      return loadMatchesSuccess({
        data: filteredMatches.sort((a, b) => {
          // SWD-1336 fix sort
            if (!a.startDate) return 1;
            if (!b.startDate) return -1;

            return new Date(a.startDate).getTime() - new Date(b.startDate).getTime();
          }),
        });

    }),
    catchError(error => of(loadMatchesFailure(error))),
  ), {dispatch: true});

  constructor(
    private actions$: Actions,
    private streamPlayService: StreamPlayService,
    private store: Store<AppState>,
  ) {
  }

  private isOneTeamInMap(fixture: StreamPlayItem, teams: Map<string, Team>): boolean {
    // get matches with a team in map
    return this.checkSize(fixture, teams) === 1;
  }

  private areBothTeamsInMap(fixture: StreamPlayItem, teams: Map<string, Team>): boolean {
    // get matches with both teams in map
    return this.checkSize(fixture, teams) === 2;
  }

  private checkSize(fixture: StreamPlayItem, teams: Map<string, Team>): number {
    const competitors = new Set();
    teams?.forEach((team) => {
      team.teamName === fixture?.homeTeam?.name ? competitors.add(fixture.homeTeam.name): null;
      team.teamName === fixture?.awayTeam?.name ? competitors.add(fixture.awayTeam.name): null;
    });
    return competitors.size;
  }

  private filterMatches(fixtures: StreamPlayItem[], teams: Map<string, Team>): StreamPlayItem[] {
    return fixtures.filter((fixture) => {
      return this.areBothTeamsInMap(fixture, teams);      // get matches with both teams set in WP
    });
  }

  private filterPriorityMatches(fixtures: StreamPlayItem[], teams: Map<string, Team>, priorityTeams: Map<string, Team>): StreamPlayItem[] {
    return fixtures.filter((fixture) => {
      return this.isOneTeamInMap(fixture, priorityTeams)  // get matches with priority teams set in WP
        && this.areBothTeamsInMap(fixture, teams);      // get matches with both teams set in WP
    });
  }
}
